'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ADVISOR_GOAL_OPTS, ADVISOR_LOSS_OPTS, ADVISOR_MODE_OPTS,
  advisorCopy, runAdvisor, templateName,
  RISK_COLOR, riskLabel,
  type AdvisorGoal, type AdvisorLoss, type AdvisorMode, type AdvisorResult,
} from '@/lib/strategy';

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function sampleLabels(zh: boolean) {
  return {
    totalReturn: zh ? '总收益' : 'Total return',
    benchmark: zh ? '基准' : 'Benchmark',
    maxDrawdown: zh ? '最大回撤' : 'Max drawdown',
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  zh: boolean;
}

export default function StrategyAdvisor({ open, onClose, zh }: Props) {
  const router = useRouter();
  const c = advisorCopy(zh);
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState<AdvisorGoal>('balanced');
  const [loss, setLoss] = useState<AdvisorLoss>('mid');
  const [mode, setMode] = useState<AdvisorMode>('spot');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdvisorResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setStep(1); setResult(null); setErr(null); setLoading(false); }
  }, [open]);

  if (!open) return null;

  const pick = (sel: boolean) => {
    const cl = 'cursor-pointer rounded-xl border px-3 py-2.5 text-sm transition-colors';
    return `${cl} ${
      sel ? 'border-gf-accent bg-gf-accent/10 text-gf-text' : 'border-gf-border bg-gf-card text-gf-muted hover:border-gf-accent/60'
    }`;
  };

  const submit = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await runAdvisor({ goal, lossTolerance: loss, mode });
      setResult(r); setStep(4);
    } catch (e: any) {
      setErr(e?.message ?? 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-gf-bg text-gf-text shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">{c.title}</h2>
            <p className="mt-0.5 text-xs text-gf-muted">{c.subtitle}</p>
          </div>
          <button onClick={onClose} aria-label="close" className="ml-3 rounded-lg px-2 py-1 text-gf-muted hover:bg-gf-card">✕</button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {step === 1 && (
            <div className="space-y-2.5">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium">{c.goalQ}</span>
                <span className="text-xs text-gf-muted">{c.stepGoal}</span>
              </div>
              {ADVISOR_GOAL_OPTS.map((o) => (
                <button key={o.value} onClick={() => setGoal(o.value)} className={`${pick(goal === o.value)} w-full text-left`}>
                  <span className="block font-medium">{zh ? o.zhHant : o.en}</span>
                </button>
              ))}
              <button onClick={() => setStep(2)} className="mt-3 w-full rounded-xl bg-gf-accent py-2.5 text-sm font-semibold text-white">
                {zh ? '下一步' : 'Next'}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2.5">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium">{c.lossQ}</span>
                <span className="text-xs text-gf-muted">{c.stepLoss}</span>
              </div>
              {ADVISOR_LOSS_OPTS.map((o) => (
                <button key={o.value} onClick={() => setLoss(o.value)} className={`${pick(loss === o.value)} w-full text-left`}>
                  <span className="block font-medium">{zh ? o.zhHant : o.en}</span>
                </button>
              ))}
              <div className="mt-3 flex gap-2">
                <button onClick={() => setStep(1)} className="flex-1 rounded-xl border border-gf-border py-2.5 text-sm text-gf-muted">{c.back}</button>
                <button onClick={() => setStep(3)} className="flex-1 rounded-xl bg-gf-accent py-2.5 text-sm font-semibold text-white">{zh ? '下一步' : 'Next'}</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2.5">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium">{c.modeQ}</span>
                <span className="text-xs text-gf-muted">{c.stepMode}</span>
              </div>
              {ADVISOR_MODE_OPTS.map((o) => (
                <button key={o.value} onClick={() => setMode(o.value)} className={`${pick(mode === o.value)} w-full text-left`}>
                  <span className="block font-medium">{zh ? o.zhHant : o.en}</span>
                </button>
              ))}
              <div className="mt-3 flex gap-2">
                <button onClick={() => setStep(2)} className="flex-1 rounded-xl border border-gf-border py-2.5 text-sm text-gf-muted">{c.back}</button>
                <button onClick={submit} disabled={loading} className="flex-1 rounded-xl bg-gf-accent py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                  {loading ? c.loading : c.getResult}
                </button>
              </div>
              {err && <p className="text-xs text-gf-negative">{err}</p>}
            </div>
          )}

          {step === 4 && result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">{c.resultTitle}</h3>
                <button onClick={() => { setResult(null); setStep(1); }} className="text-xs text-gf-muted underline">{c.reset}</button>
              </div>
              {result.candidates.length === 0 && (
                <p className="rounded-xl border border-gf-border bg-gf-card p-4 text-sm text-gf-muted">{c.noCandidates}</p>
              )}
              {result.candidates.map((cd) => (
                <div key={cd.templateKey} className="rounded-xl border border-gf-border bg-gf-card p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: RISK_COLOR[cd.risk] ?? '#888' }} />
                      <span className="text-sm font-semibold">{templateName(cd.templateKey, zh)}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cd.marketType === 'futures' ? 'bg-gf-accent/15 text-gf-accent' : 'bg-gf-border/60 text-gf-muted'}`}>
                        {zh ? (cd.marketType === 'futures' ? '合约' : '现货') : cd.marketType === 'futures' ? 'Futures' : 'Spot'}
                      </span>
                    </div>
                    <span className="text-xs text-gf-muted">{riskLabel(zh, cd.risk)}</span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-gf-muted">
                    <span className="text-gf-text">{c.matchReason}: </span>{zh ? cd.matchReason.zhHant : cd.matchReason.en}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-gf-muted">
                    <span className="text-gf-warn">{c.caution}: </span>{zh ? cd.caution.zhHant : cd.caution.en}
                  </p>
                  {cd.sample && (
                    <div className="mt-2.5 grid grid-cols-3 gap-2 rounded-lg border border-gf-border/70 bg-gf-bg/60 p-2 text-center">
                      <div>
                        <div className="text-[10px] text-gf-muted">{sampleLabels(zh).totalReturn}</div>
                        <div className="text-sm font-semibold text-gf-text">{fmtPct(cd.sample.totalReturn)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gf-muted">{sampleLabels(zh).benchmark}</div>
                        <div className="text-sm font-semibold text-gf-text">{fmtPct(cd.sample.benchmarkReturn)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gf-muted">{sampleLabels(zh).maxDrawdown}</div>
                        <div className="text-sm font-semibold text-gf-text">{fmtPct(-(cd.sample.maxDrawdown ?? 0))}</div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => { onClose(); router.push(`/strategy/${cd.templateKey}`); }}
                    className="mt-2.5 w-full rounded-lg bg-gf-accent py-2 text-xs font-semibold text-white"
                  >
                    {c.goConfig} →
                  </button>
                </div>
              ))}
              <p className="rounded-xl border border-gf-border/60 bg-gf-card/50 p-2.5 text-[11px] leading-relaxed text-gf-muted">
                <span className="font-medium">{c.disclaimerLabel}: </span>{zh ? result.disclaimer.zhHant : result.disclaimer.en}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
